import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  UserPlus, Trash2, Shield, Building2, CreditCard,
  Users, Lock, Clock, Mail, Pencil, Check, X,
  Phone, CheckCircle2, RefreshCw, Activity
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { PageHeader } from '../components/common/PageHeader';
import { selectUser, selectPlan, setUser } from '../store/authSlice';
import { authService } from '../services/auth.service';
import apiClient from '../services/apiClient';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RoleManager from '../components/setting/RoleManager';
import ForgotPasswordDialog from '../components/setting/ForgotPasswordDialog';
import ActivityLog from '../components/setting/ActivityLog'; // ← NEW

const teamsApi = {
  getRoles:   () => apiClient.get('/api/teams/roles').then(r => r.data),
  getMembers: () => apiClient.get('/api/teams/members').then(r => r.data),
  invite:     (d) => apiClient.post('/api/teams/invite', d).then(r => r.data),
  revoke:     (id) => apiClient.delete(`/api/teams/invite/${id}`).then(r => r.data),
  remove:     (id) => apiClient.delete(`/api/teams/members/${id}`).then(r => r.data),
};

const settingsApi = {
  update:    (d) => apiClient.patch('/api/settings/business', d).then(r => r.data),
  sendOTP:   (phone, channel) => apiClient.post('/api/auth/send-otp', { phone, channel }).then(r => r.data),
  verifyOTP: (phone, code)    => apiClient.post('/api/auth/verify-otp', { phone, code }).then(r => r.data),
};

// ── Editable field ────────────────────────────────────────────────────────────
function EditableField({ label, value, onSave, type = 'text', disabled = false }) {
  const [editing, setEditing] = useState(false);
  const [val,     setVal]     = useState(value || '');
  const [saving,  setSaving]  = useState(false);

  const handleSave = async () => {
    if (val === value) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(val); setEditing(false); } catch {}
    setSaving(false);
  };
  const handleCancel = () => { setVal(value || ''); setEditing(false); };

  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-600">{label}</Label>
      {editing ? (
        <div className="flex gap-2">
          <Input value={val} onChange={e => setVal(e.target.value)} type={type}
            className="flex-1" autoFocus
            onKeyDown={e => { if (e.key==='Enter') handleSave(); if (e.key==='Escape') handleCancel(); }} />
          <Button size="sm" onClick={handleSave} disabled={saving}
            className="h-9 w-9 p-0 bg-emerald-600 hover:bg-emerald-700 text-white">
            {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancel} className="h-9 w-9 p-0">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      ) : (
        <div className="flex gap-2 items-center group">
          <Input value={value || '—'} disabled className="bg-slate-50 text-slate-700 flex-1" />
          {!disabled && (
            <Button size="sm" variant="ghost" onClick={() => { setVal(value||''); setEditing(true); }}
              className="h-9 w-9 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
              <Pencil className="w-3.5 h-3.5 text-slate-400" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Phone Verify Dialog ───────────────────────────────────────────────────────
function PhoneVerifyDialog({ open, phone, onClose, onVerified }) {
  const [step,     setStep]      = useState('input');
  const [mobile,   setMobile]    = useState(phone || '');
  const [channel,  setChannel]   = useState('sms');
  const [otp,      setOtp]       = useState('');
  const [sending,  setSending]   = useState(false);
  const [verifying,setVerifying] = useState(false);
  const [countdown,setCountdown] = useState(0);

  const startCountdown = () => {
    setCountdown(30);
    const t = setInterval(() => {
      setCountdown(c => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; });
    }, 1000);
  };

  const handleSendOTP = async () => {
    if (!/^[6-9]\d{9}$/.test(mobile)) { toast.error('Enter a valid 10-digit Indian mobile number'); return; }
    setSending(true);
    try {
      await settingsApi.sendOTP(mobile, channel);
      toast.success(`OTP sent via ${channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}`);
      setStep('otp'); startCountdown();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to send OTP'); }
    setSending(false);
  };

  const handleVerify = async () => {
    if (otp.length < 4) { toast.error('Enter the OTP'); return; }
    setVerifying(true);
    try {
      await settingsApi.verifyOTP(mobile, otp);
      toast.success('Phone number verified!');
      onVerified(mobile); onClose();
    } catch (e) { toast.error(e.response?.data?.message || 'Invalid OTP'); }
    setVerifying(false);
  };

  const reset = () => { setStep('input'); setOtp(''); setCountdown(0); };

  return (
    <Dialog open={open} onOpenChange={() => { reset(); onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Phone className="w-5 h-5 text-[#2E6DA4]" />
            {step === 'input' ? 'Verify Phone Number' : 'Enter OTP'}
          </DialogTitle>
          <DialogDescription>
            {step === 'input' ? 'Enter your mobile number to receive a verification code.' : `OTP sent to +91 ${mobile}.`}
          </DialogDescription>
        </DialogHeader>
        {step === 'input' ? (
          <div className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Mobile Number</Label>
              <div className="flex gap-2">
                <div className="flex items-center justify-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600 font-mono">+91</div>
                <Input value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g,'').slice(0,10))}
                  placeholder="9876543210" className="flex-1 font-mono" onKeyDown={e => e.key==='Enter' && handleSendOTP()} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Send via</Label>
              <div className="grid grid-cols-2 gap-2">
                {[{value:'sms',label:'💬 SMS'},{value:'whatsapp',label:'📱 WhatsApp'}].map(ch => (
                  <button key={ch.value} onClick={() => setChannel(ch.value)}
                    className={['py-2.5 rounded-xl text-sm font-semibold border-2 transition-all',
                      channel===ch.value ? 'bg-[#2E6DA4] text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-[#2E6DA4]'
                    ].join(' ')}>{ch.label}</button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">6-digit OTP</Label>
              <Input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                placeholder="••••••" className="text-center text-2xl font-mono tracking-[0.5em] h-12"
                maxLength={6} autoFocus onKeyDown={e => e.key==='Enter' && handleVerify()} />
            </div>
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>Didn't receive it?</span>
              {countdown > 0 ? <span>Resend in {countdown}s</span> :
                <button onClick={() => { setStep('input'); setOtp(''); }} className="text-[#2E6DA4] hover:underline">Try again</button>}
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          {step === 'input' ? (
            <Button onClick={handleSendOTP} disabled={sending} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">
              {sending ? 'Sending…' : 'Send OTP'}
            </Button>
          ) : (
            <Button onClick={handleVerify} disabled={verifying || otp.length < 6} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {verifying ? 'Verifying…' : 'Verify'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Invite Modal ──────────────────────────────────────────────────────────────
function InviteModal({ open, onClose, roles }) {
  const qc = useQueryClient();
  const [email,  setEmail]   = useState('');
  const [roleId, setRoleId]  = useState('');
  const [mobile, setMobile]  = useState('');
  const [sending,setSending] = useState(false);

  const handleInvite = async () => {
    if (!email || !roleId) { toast.error('Fill email and role'); return; }
    setSending(true);
    try {
      const res = await teamsApi.invite({ email, roleId, mobile: mobile || undefined });
      toast.success('Invite created!', {
        description: mobile ? `WhatsApp invite sent to +91${mobile}` : `Share this link: ${res.emailPayload?.invite_link}`,
        duration: 8000,
      });
      qc.invalidateQueries({ queryKey: ['teamMembers'] });
      setEmail(''); setRoleId(''); setMobile(''); onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invite');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#2E6DA4]" /> Invite Team Member
          </DialogTitle>
          <DialogDescription>If you add their mobile number, they'll get a WhatsApp invite automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="colleague@business.com" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-600">Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent>
                {(roles || []).map(r => (
                  <SelectItem key={r._id} value={r._id}>
                    <div><div className="font-medium">{r.name}</div>
                    {r.description && <div className="text-xs text-slate-400">{r.description}</div>}</div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-600">Mobile (optional — for WhatsApp invite)</Label>
            <div className="flex gap-2">
              <div className="flex items-center justify-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600 font-mono">+91</div>
              <Input value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g,'').slice(0,10))} placeholder="9876543210" className="flex-1 font-mono" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={sending} onClick={handleInvite} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">
            {sending ? 'Sending…' : 'Send Invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Settings Page ────────────────────────────────────────────────────────
export default function Settings() {
  const dispatch   = useDispatch();
  const user       = useSelector(selectUser);
  const plan       = useSelector(selectPlan);
  const navigate   = useNavigate();
  const qc         = useQueryClient();
  const [params]   = useSearchParams();
  const defaultTab = params.get('tab') || 'business';
  const isPro      = plan === 'pro';

  const [phoneVerifyOpen, setPhoneVerifyOpen] = useState(false);
  const [forgotOpen,      setForgotOpen]      = useState(false);

  const updateMut = useMutation({
    mutationFn: (d) => settingsApi.update(d),
    onSuccess:  (res) => {
      toast.success('Saved');
      if (res.data) dispatch(setUser({ ...user, ...res.data }));
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to save'),
  });
  const saveField = (key) => async (value) => { await updateMut.mutateAsync({ [key]: value }); };

  const [pwForm, setPwForm] = useState({ currentPassword:'', newPassword:'', confirm:'' });
  const changePwMut = useMutation({
    mutationFn: (d) => authService.changePassword(d),
    onSuccess:  () => { toast.success('Password changed — please log in again'); navigate('/login'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const { data: rolesData }   = useQuery({ queryKey:['teamRoles'],   queryFn: teamsApi.getRoles,   enabled: isPro });
  const { data: membersData, isLoading: membersLoading } = useQuery({ queryKey:['teamMembers'], queryFn: teamsApi.getMembers, enabled: isPro });
  const roles   = rolesData?.data   || [];
  const members = membersData?.data?.members || [];
  const pending = membersData?.data?.pending || [];

  const revokeMut = useMutation({ mutationFn: teamsApi.revoke, onSuccess: () => { toast.success('Revoked'); qc.invalidateQueries({ queryKey:['teamMembers'] }); } });
  const removeMut = useMutation({ mutationFn: teamsApi.remove, onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey:['teamMembers'] }); } });

  // All tabs including the new Activity Log
  const TABS = [
    ['business',  'Business'],
    ['security',  'Security'],
    ['billing',   'Plan & Billing'],
    ['team',      'Team'],
    ['roles',     'Roles'],
    ['activity',  'Activity Log'],  // ← NEW
  ];

  return (
    <div>
      <PageHeader title="Settings" description="Manage your workspace, team, and security." />

      <Tabs defaultValue={defaultTab} className="space-y-5">
        <TabsList className="bg-white border border-slate-200 p-1 h-auto flex-wrap gap-1">
          {TABS.map(([v,l]) => (
            <TabsTrigger key={v} value={v}
              className="data-[state=active]:bg-[#2E6DA4] data-[state=active]:text-white px-4 py-2 text-sm">
              {v === 'activity' && <Activity className="w-3.5 h-3.5 mr-1.5" />}
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ═══ BUSINESS ═══ */}
        <TabsContent value="business">
          <Card className="p-5 sm:p-6 border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-5">
              <Building2 className="w-5 h-5 text-[#2E6DA4]" />
              <h3 className="text-base font-semibold text-slate-900 font-heading">Business Profile</h3>
              <span className="text-xs text-slate-400 ml-1">— hover a field to edit</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <EditableField label="Business Name" value={user?.businessName} onSave={saveField('businessName')} />
              <EditableField label="Owner Name"    value={user?.ownerName}    onSave={saveField('ownerName')} />
              <EditableField label="Email"         value={user?.email}        disabled />
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-600">Phone</Label>
                <div className="flex gap-2 items-center">
                  <div className="relative flex-1">
                    <Input value={user?.phone || '—'} disabled className="bg-slate-50 text-slate-700 pr-24" />
                    {user?.isPhoneVerified && (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] text-emerald-600 font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> Verified
                      </span>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setPhoneVerifyOpen(true)}
                    className="h-9 shrink-0 text-xs border-[#2E6DA4] text-[#2E6DA4] hover:bg-[#2E6DA4] hover:text-white">
                    <Phone className="w-3.5 h-3.5 mr-1" />
                    {user?.phone ? 'Change' : 'Add & Verify'}
                  </Button>
                </div>
              </div>
              <EditableField label="State"      value={user?.state}     onSave={saveField('state')} />
              <EditableField label="State Code" value={user?.stateCode} onSave={saveField('stateCode')} />
              <EditableField label="GSTIN"      value={user?.gstin}     onSave={saveField('gstin')} />
              <EditableField label="Plan"       value={user?.planLabel || 'Starter'} disabled />
            </div>
            <div className="mt-6 pt-5 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Payroll Configuration</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  ['Working Days / Month', user?.businessConfig?.workingDaysPerMonth ?? 26],
                  ['Overtime Multiplier',  `${user?.businessConfig?.overtimeMultiplier ?? 1.5}×`],
                  ['Fiscal Year Start',    `Month ${user?.businessConfig?.fiscalYearStartMonth ?? 4} (April)`],
                ].map(([label, val]) => (
                  <div key={label} className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-slate-600">{label}</Label>
                    <Input value={val} disabled className="bg-slate-50 text-slate-700" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-3">Contact support to change payroll configuration.</p>
            </div>
          </Card>
        </TabsContent>

        {/* ═══ SECURITY ═══ */}
        <TabsContent value="security" className="space-y-4">
          <Card className="p-5 sm:p-6 border-slate-200 shadow-sm max-w-md">
            <div className="flex items-center gap-2 mb-5">
              <Lock className="w-5 h-5 text-[#2E6DA4]" />
              <h3 className="text-base font-semibold text-slate-900 font-heading">Change Password</h3>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (pwForm.newPassword !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
              if (pwForm.newPassword.length < 8) { toast.error('Min 8 characters'); return; }
              changePwMut.mutate({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
            }} className="space-y-4">
              {[['currentPassword','Current Password'],['newPassword','New Password'],['confirm','Confirm New Password']].map(([f,l]) => (
                <div key={f} className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-600">{l}</Label>
                  <Input type="password" value={pwForm[f]} onChange={e => setPwForm({...pwForm,[f]:e.target.value})} />
                </div>
              ))}
              <Button type="submit" disabled={changePwMut.isPending} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white w-full">
                {changePwMut.isPending ? 'Changing…' : 'Change Password'}
              </Button>
              <button type="button" onClick={() => setForgotOpen(true)}
                className="text-xs text-[#2E6DA4] hover:underline w-full text-center block mt-1">
                Forgot current password? Reset via OTP →
              </button>
            </form>
          </Card>

          <Card className="p-5 sm:p-6 border-slate-200 shadow-sm max-w-md">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-slate-400" />
              <h3 className="text-base font-semibold text-slate-900 font-heading">Two-Factor Authentication</h3>
            </div>
            <p className="text-xs text-slate-500 mb-3">Add an extra layer of security with SMS OTP on each login.</p>
            <div className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
              <div>
                <div className="text-sm font-medium text-slate-700">SMS OTP via Twilio</div>
                <div className="text-xs text-slate-400">Coming soon — Twilio Verify API</div>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold uppercase">Soon</span>
            </div>
          </Card>

          <Card className="p-5 sm:p-6 border-slate-200 shadow-sm max-w-md">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-slate-400" />
              <h3 className="text-base font-semibold text-slate-900 font-heading">Recent Activity</h3>
            </div>
            <div className="text-sm text-slate-600">
              Last login: {user?.lastLoginAt
                ? new Date(user.lastLoginAt).toLocaleString('en-IN',{dateStyle:'medium',timeStyle:'short'})
                : 'This session'}
            </div>
            <button onClick={() => {/* navigate to activity tab */
              const tab = document.querySelector('[data-value="activity"]');
              if (tab) tab.click();
            }} className="text-xs text-[#2E6DA4] hover:underline mt-2 block">
              View full activity log →
            </button>
          </Card>
        </TabsContent>

        {/* ═══ BILLING ═══ */}
        <TabsContent value="billing">
          <Card className="p-5 sm:p-6 border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-5">
              <CreditCard className="w-5 h-5 text-[#2E6DA4]" />
              <h3 className="text-base font-semibold text-slate-900 font-heading">Plan & Billing</h3>
            </div>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6 ${isPro?'bg-[#2E6DA4] text-white':'bg-slate-100 text-slate-700'}`}>
              {isPro ? '★ Pro Plan' : '⬡ Starter Plan'}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {[
                ['Current Plan',  isPro?'Pro — ₹1,299/month':'Starter — ₹499/month'],
                ['Employees',     isPro?'Unlimited':'Up to 8'],
                ['Team Members',  isPro?'Up to 10':'Not included'],
                ['Features',      isPro?'All features + exports + team':'Core payroll'],
              ].map(([l,v]) => (
                <div key={l} className="space-y-1">
                  <div className="text-xs uppercase tracking-wider text-slate-500">{l}</div>
                  <div className="text-sm font-medium text-slate-900">{v}</div>
                </div>
              ))}
            </div>
            {!isPro && <Button onClick={() => navigate('/pricing')} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">Upgrade to Pro</Button>}
          </Card>
        </TabsContent>

        {/* ═══ TEAM ═══ */}
        <TabsContent value="team">
          {!isPro ? (
            <Card className="p-10 border-dashed border-slate-200 text-center">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="text-sm font-medium text-slate-600 mb-1">Team access requires Pro plan</div>
              <div className="text-xs text-slate-400 mb-4">Upgrade to invite HR managers, accountants, and viewers.</div>
              <Button onClick={() => navigate('/pricing')} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">Upgrade to Pro</Button>
            </Card>
          ) : (
            <div className="space-y-4">
              <Card className="border-slate-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-slate-200">
                  <h3 className="text-base font-semibold text-slate-900 font-heading">Team Members</h3>
                  <Button onClick={() => setInviteOpen(true)} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white h-9">
                    <UserPlus className="w-4 h-4 mr-1.5" /> Invite
                  </Button>
                </div>
                {membersLoading ? (
                  <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
                ) : members.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-sm">No team members yet.</div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>{['Name','Email','Role','Joined','Actions'].map(h=><th key={h} className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-left">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {members.map(m=>(
                        <tr key={m._id} className="hover:bg-slate-50">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#2E6DA4] to-[#1E3A5F] flex items-center justify-center text-white text-[10px] font-semibold">
                                {(m.email||'').slice(0,2).toUpperCase()}
                              </div>
                              <span className="text-sm font-medium text-slate-900">{m.memberUserId?.name||'—'}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-sm text-slate-600">{m.email}</td>
                          <td className="px-5 py-3.5"><span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">{m.roleId?.name||'—'}</span></td>
                          <td className="px-5 py-3.5 text-xs text-slate-400">{m.acceptedAt?new Date(m.acceptedAt).toLocaleDateString('en-IN'):'—'}</td>
                          <td className="px-5 py-3.5">
                            <Button variant="ghost" size="sm" onClick={()=>removeMut.mutate(m.memberUserId?._id)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
              {pending.length > 0 && (
                <Card className="border-slate-200 shadow-sm overflow-hidden">
                  <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-200 bg-amber-50">
                    <Mail className="w-4 h-4 text-amber-600" />
                    <h4 className="text-sm font-semibold text-amber-800">Pending Invites ({pending.length})</h4>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {pending.map(inv=>(
                      <div key={inv._id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{inv.email}</div>
                          <div className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
                            <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{inv.roleId?.name}</span>
                            Expires {new Date(inv.expiresAt).toLocaleDateString('en-IN')}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={()=>revokeMut.mutate(inv._id)} className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50">Revoke</Button>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* ═══ ROLES ═══ */}
        <TabsContent value="roles">
          {!isPro ? (
            <Card className="p-10 border-dashed border-slate-200 text-center">
              <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="text-sm font-medium text-slate-600 mb-1">Role management requires Pro plan</div>
              <Button onClick={() => navigate('/pricing')} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white mt-2">Upgrade to Pro</Button>
            </Card>
          ) : <RoleManager />}
        </TabsContent>

        {/* ═══ ACTIVITY LOG ═══ */}
        <TabsContent value="activity">
          <ActivityLog />
        </TabsContent>

      </Tabs>

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} roles={roles} />
      <PhoneVerifyDialog
        open={phoneVerifyOpen}
        phone={user?.phone || ''}
        onClose={() => setPhoneVerifyOpen(false)}
        onVerified={(mobile) => dispatch(setUser({ ...user, phone: mobile, isPhoneVerified: true }))}
      />
      <ForgotPasswordDialog open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
}